import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import useInterval from 'beautiful-react-hooks/useInterval';
import React from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import config from '../config';
import {
  getPersistedOrganisationId,
  subscribeToOrganisationIdUpdates,
} from './persistedOrganisationId';

export default function ConnectWebsocketProvider({ children }: { children: React.ReactNode }) {
  const [organisationId, setOrganisationId] = React.useState<string | null>(
    () => getPersistedOrganisationId(),
  );
  const [idToken, setIdToken] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    fetchAuthSession()
      .then((session) => {
        const token = session.tokens?.idToken?.toString();
        setIdToken(token);
      })
      .catch((err) => { console.error('initial fetchAuthSession error:', err); });
  }, []);

  React.useEffect(() => Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'signedIn' || payload.event === 'tokenRefresh') {
      fetchAuthSession()
        .then((session) => { setIdToken(session.tokens?.idToken?.toString()); })
        .catch((err) => { console.error('fetchAuthSession after auth event error:', err); });
    }
    if (payload.event === 'signedOut') {
      setIdToken(undefined);
    }
  }), []);

  React.useEffect(() => subscribeToOrganisationIdUpdates((orgId) => {
    setOrganisationId(orgId);
  }), []);

  const wsUrl = React.useMemo(() => {
    if (!organisationId) {
      return null;
    }
    const url = new URL(config.Live.ConnectWebSocketUrl);
    url.searchParams.set('organisationId', organisationId);
    return url.toString();
  }, [organisationId]);

  const { sendJsonMessage, readyState, lastJsonMessage } = useWebSocket<{ type: string }>(
    wsUrl,
    {
      reconnectAttempts: Infinity,
      shouldReconnect: () => true,
      reconnectInterval(lastAttemptNumber) {
        if (lastAttemptNumber < 10) return 500;
        return Math.min(2 ** (lastAttemptNumber - 10) * 1000, 60000);
      },
      onError: (event) => { console.error('WebSocket error:', event); },
    },
    wsUrl !== null,
  );

  React.useEffect(() => {
    if (lastJsonMessage?.type === 'refresh-application') {
      window.location.reload();
    }
  }, [lastJsonMessage]);

  // Authenticate with a token fetched now, not with the one cached in state. The cached
  // token only updates on a Hub signedIn/tokenRefresh event, so a tab that was suspended
  // past expiry kept re-sending the same dead token on every reconnect - the server logged
  // TokenExpiredError with an unchanging expiredAt for hours and the connection was never
  // authenticated. fetchAuthSession refreshes transparently when the token has expired.
  React.useEffect(() => {
    if (readyState !== ReadyState.OPEN) return;
    (async () => {
      let freshIdToken: string | undefined;
      try {
        const session = await fetchAuthSession();
        freshIdToken = session.tokens?.idToken?.toString();
      } catch (err) {
        console.error('fetchAuthSession before websocket auth error:', err);
        return;
      }
      if (!freshIdToken) return;

      const dualChannelAppVersion = (await window.utilsAPI?.getVersion()) ?? undefined;
      sendJsonMessage({
        type: 'auth',
        idToken: freshIdToken,
        appVersion: config.AppVersion,
        ...(dualChannelAppVersion != null && { dualChannelAppVersion }),
      });
    })();
    // idToken stays in the dependency list so a Hub token refresh re-authenticates the
    // open connection, even though the value sent is re-fetched above.
  }, [readyState, idToken]);

  useInterval(() => {
    if (readyState === ReadyState.OPEN) {
      sendJsonMessage({ type: 'ping' });
    }
  }, 60000);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
